/**
 * Sport bet limit groups — BO 1.8 Bet Limit. See AIO-81599.
 * Mirrors the api's /betlimitgroup responses.
 */

export interface BetLimitSport {
  id: number;
  code: string;
  label: string;
  position: number;
}

export interface BetLimitBetType {
  code: string;
  label: string;
  position: number;
}

export interface BetLimitCurrency {
  id: number;
  name: string;
}

export interface BetLimitProviderOption {
  game_provider_id: number;
  code: string;
  name: string;
  grain: number;
  grain_name: string;
  has_min: number;
  has_max: number;
  has_cap: number;
  allow_clear: number;
  hard_max: string | null;
  note: string | null;
  default_group_id: number | null;
  currencies: BetLimitCurrency[];
  sports: BetLimitSport[];
  bet_types: BetLimitBetType[];
}

export interface BetLimitGroupRow {
  id: number;
  code: string;
  name: string;
  is_default: number;
  game_provider_id: number;
  game_provider_code: string;
  grain_name: string;
  sport_count: number;
  currencies: string[];
  members_count: number;
  status: number;
  status_name: string;
  remarks: string | null;
  created_by: string | null;
  created_at: string | null;
  updated_by: string | null;
  updated_at: string | null;
}

/** One cell of the grid. null = use the provider default. Amounts travel as strings. */
export interface BetLimitSetting {
  settings_currency_id: number;
  bet_limit_provider_sport_id: number;
  bet_type_code: string;
  min_bet: string | null;
  max_bet: string | null;
  max_amount_per_match: string | null;
}

export interface BetLimitGroupDetail {
  group: BetLimitGroupRow;
  settings: BetLimitSetting[];
  members_count: number;
}

export interface BetLimitMemberRow {
  member_account_id: number;
  username: string;
  currency_code: string;
  member_group: string | null;
  added_by: string | null;
  added_at: string | null;
}

export interface BetLimitCandidate {
  id: number;
  username: string;
  settings_currency_id: number;
  currency_code: string;
  member_group: string | null;
  current_group_id: number | null;
  current_group_name: string | null;
}

/** A player GSYS did not assign on save (e.g. no SBO2 game account yet); not saved in the group. */
export interface BetLimitNotAdded {
  member_account_id: number;
  username: string;
  reason: string;
}

export interface BetLimitGroupPayload {
  name: string;
  game_provider_id?: number;
  status: number;
  remarks: string | null;
  settings: BetLimitSetting[];
  members: { add: number[]; remove: number[] };
}

export const BET_LIMIT_AMOUNT_FIELDS: { key: 'min_bet' | 'max_bet' | 'max_amount_per_match'; label: string; flag: 'has_min' | 'has_max' | 'has_cap' }[] = [
  { key: 'min_bet', label: 'Min per Bet', flag: 'has_min' },
  { key: 'max_bet', label: 'Max per Bet', flag: 'has_max' },
  { key: 'max_amount_per_match', label: 'Max per Match', flag: 'has_cap' },
];

/** All Sports (SBO2 sport_type 0). */
export const BET_LIMIT_SPORT_ALL = '0';

/** SBO2 Mix Parlay (market_type 40): GSYS accepts it only on All Sports (handover v1.2). */
export const BET_LIMIT_MIX_PARLAY = '40';

/** Same key the api uses to diff cells: currency|sport|bet type. */
export function betLimitCellKey(currencyId: number, sportId: number, betTypeCode: string): string {
  return `${+currencyId}|${+sportId}|${betTypeCode}`;
}

// GSYS accepts whole numbers only.
const AMOUNT_PATTERN = /^\d{1,22}$/;

export function hasBetLimitValue(cell: BetLimitSetting): boolean {
  return BET_LIMIT_AMOUNT_FIELDS.some(f => cell[f.key] !== null && cell[f.key] !== '');
}

/**
 * The same checks as the api's BetLimitSettingsGrid, so a bad cell is caught before Save:
 * a whole number of 0 or more, at or below the vendor maximum, and
 * min per bet <= max per bet <= max per match among the amounts the cell sets.
 */
export function betLimitCellError(cell: BetLimitSetting, provider: BetLimitProviderOption): string | null {
  const values: { [key: string]: number } = {};

  for (const field of BET_LIMIT_AMOUNT_FIELDS) {
    const raw = cell[field.key];
    if (raw === null || raw === '') {
      continue;
    }
    if (!AMOUNT_PATTERN.test(raw)) {
      return `${field.label} must be a whole number.`;
    }
    if (provider.hard_max !== null && +raw > +provider.hard_max) {
      return `${field.label} must not exceed ${(+provider.hard_max).toLocaleString('en-US')}.`;
    }
    values[field.key] = +raw;
  }

  const pairs: ['min_bet' | 'max_bet', 'max_bet' | 'max_amount_per_match'][] = [
    ['min_bet', 'max_bet'], ['max_bet', 'max_amount_per_match'], ['min_bet', 'max_amount_per_match'],
  ];
  for (const [lower, upper] of pairs) {
    if (values[lower] !== undefined && values[upper] !== undefined && values[lower] > values[upper]) {
      const label = (key: string) => BET_LIMIT_AMOUNT_FIELDS.find(f => f.key === key).label;
      return `${label(lower)} must not exceed ${label(upper)}.`;
    }
  }

  return null;
}
